import { MapPinIcon } from "@heroicons/react/24/outline";
import { MouseEventHandler, useEffect, useState } from "react";
import classNames from "src/helpers/classNames";
import { StoreLocation } from "src/types/store-locations";
import { useTranslations } from "next-intl";
import ThemeLink from "../link/ThemeLink";

interface StoreLocationCardProps {
  location: StoreLocation;
  onClick: MouseEventHandler<HTMLDivElement>;
  selectedLocation?: StoreLocation | null;
}

const StoreLocationCard = ({
  location,
  onClick,
  selectedLocation,
}: StoreLocationCardProps) => {
  const t = useTranslations("StoreLocator");
  const [isOpen, setIsOpen] = useState(false);

  const isStoreOpen = () => {
    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    if (
      currentHours > location.openingHours.hours ||
      (currentHours === location.openingHours.hours &&
        currentMinutes >= location.openingHours.minutes)
    ) {
      setIsOpen(true);
    } else if (
      currentHours < location.closingHours.hours ||
      (currentHours === location.closingHours.hours &&
        currentMinutes <= location.closingHours.minutes)
    ) {
      setIsOpen(false);
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    isStoreOpen();
  }, []);

  return (
    <div
      className={classNames(
        "flex cursor-pointer justify-between border-beige-700 bg-gray-100 p-5 shadow-sm transition-all duration-200 ease-in-out hover:border-t-8 hover:shadow-lg",
        selectedLocation?.placeId === location.placeId
          ? "border-t-8 shadow-lg"
          : "border-t-4"
      )}
      onClick={onClick}
    >
      <div className="flex flex-col items-start justify-between gap-1">
        <header className="self-start whitespace-nowrap font-bold !leading-6 lg:whitespace-normal lg:text-lg">
          {t(location.storeName)}
        </header>
        <div className="flex flex-col items-start pt-4 text-sm">
          <p>{t(location.address)}</p>
          <p className="whitespace-nowrap">
            <span
              className={classNames(
                isOpen ? "text-green-600" : "text-red-600",
                "font-semibold"
              )}
            >
              {isOpen ? t("open") : t("closed")} -
            </span>{" "}
            <span dir="ltr">{location.storeTiming}</span>
          </p>
        </div>
      </div>

      <div className="flex flex-col items-end justify-between ps-4">
        <ThemeLink
          href={location.googleMapsLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 p-2"
        >
          <MapPinIcon className="h-4 w-4 transform-gpu self-end transition-transform hover:rotate-45" />
          <span className="text-xs">{t("directions")}</span>
        </ThemeLink>
        {/* <a
          href={location.googleMapsLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center rounded-xl border border-blue-500 p-1 text-blue-500 hover:border-blue-600 hover:text-blue-600 hover:shadow-md"
        >
          <span className="inline-block">
            <MapPinIcon
              className="h-5 w-5 transform-gpu self-end transition-transform hover:rotate-45"
              fillOpacity="text-gray-900"
            />
          </span>
          <span className="text-xs">{t("directions")}</span>
        </a> */}
        {location.distanceMatrixData && (
          <span className="whitespace-nowrap text-sm" dir="ltr">
            {location.distanceMatrixData.distance?.text}
          </span>
        )}
      </div>
    </div>
  );
};

export default StoreLocationCard;
